import { useEffect, useState } from 'react';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Card, Seccion } from '@/components/Card';
import { mensajeDeError } from '@/db/tx';
import { exportarExcel, resumenExcel, nombreArchivoExcel, HOJAS_PENDIENTES } from '@/db/excel-export';
import { descargarBlob, compartirBlob, puedeCompartirArchivos } from '@/lib/file-transfer';

/**
 * Exportacion para analizar los datos en Excel.
 *
 * DISTINCION QUE LA PANTALLA DEBE DEJAR CLARA:
 *   El respaldo JSON es el oficial y se puede restaurar.
 *   Este archivo de Excel NO restaura nada: es una fotografia para analizar.
 *
 * Por eso vive en su propia seccion, con su propio aviso, y su nombre de
 * archivo termina en `.analisis.xlsx` en lugar de `.respaldo.json`.
 */
export function ExportarAnalisis() {
  const [resumen, setResumen] = useState<Array<{ nombre: string; filas: number }> | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const puedeCompartir = puedeCompartirArchivos();

  useEffect(() => {
    let vigente = true;
    void resumenExcel()
      .then((r) => {
        if (vigente) setResumen(r);
      })
      .catch(() => {
        if (vigente) setResumen(null);
      });
    return () => {
      vigente = false;
    };
  }, []);

  const totalFilas = resumen?.reduce((suma, h) => suma + h.filas, 0) ?? 0;

  async function generar(via: 'descarga' | 'compartir') {
    setTrabajando(true);
    setMensaje(null);
    setError(null);

    try {
      const blob = await exportarExcel();
      const nombre = nombreArchivoExcel();

      if (via === 'compartir') {
        const compartido = await compartirBlob(
          nombre,
          blob,
          'Datos de Control de Negocio de Comida',
        );
        if (!compartido) {
          setError(
            'No se pudo compartir el archivo. Usa "Descargar Excel", que es la via mas confiable en iPhone.',
          );
          return;
        }
        setMensaje(`Archivo compartido: ${nombre}`);
      } else {
        descargarBlob(nombre, blob);
        setMensaje(`Archivo descargado: ${nombre}. Revisalo en la app Archivos.`);
      }
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Seccion
      titulo="Exportar para analizar"
      descripcion="Un archivo de Excel con tus datos en hojas separadas, para revisarlos fuera de la aplicacion."
    >
      <Banner tono="info" titulo="Esto no es un respaldo">
        Este archivo sirve para <strong>consultar y analizar</strong>, no para restaurar. Si
        cambias de telefono o pierdes tus datos, lo que necesitas es el respaldo completo en
        JSON de la seccion anterior.
      </Banner>

      <div className="space-y-2">
        <Button
          tamano="grande"
          disabled={trabajando}
          onClick={() => {
            void generar('descarga');
          }}
        >
          {trabajando ? 'Generando…' : '📊 Descargar Excel'}
        </Button>

        {puedeCompartir && (
          <Button
            variante="secundario"
            disabled={trabajando}
            onClick={() => {
              void generar('compartir');
            }}
          >
            📤 Compartir Excel
          </Button>
        )}
      </div>

      {mensaje !== null && (
        <Banner tono="exito" titulo="Archivo generado">
          {mensaje}
        </Banner>
      )}

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo generar el archivo">
          {error}
        </Banner>
      )}

      {resumen !== null && (
        <Card>
          <h3 className="text-sm font-semibold text-texto">
            Hojas incluidas
            <span className="ml-2 font-normal text-texto-tenue">
              {totalFilas} {totalFilas === 1 ? 'renglon' : 'renglones'} en total
            </span>
          </h3>
          <ul className="mt-2 divide-y divide-borde text-sm">
            {resumen.map((h) => (
              <li key={h.nombre} className="flex justify-between gap-4 py-1.5">
                <span className="text-texto-suave">{h.nombre}</span>
                <span className="font-medium text-texto">
                  {h.filas === 0 ? '—' : h.filas}
                </span>
              </li>
            ))}
          </ul>

          {HOJAS_PENDIENTES.length > 0 && (
            <div className="mt-3 border-t border-borde pt-3">
              <h3 className="text-sm font-semibold text-texto">Hojas que faltan</h3>
              <ul className="mt-1 space-y-1.5 text-sm">
                {HOJAS_PENDIENTES.map((h) => (
                  <li key={h.nombre}>
                    <span className="font-medium text-texto">{h.nombre}</span>
                    <span className="text-texto-tenue"> · Fase {h.fase}</span>
                    <p className="text-xs text-texto-suave">{h.motivo}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
    </Seccion>
  );
}
